// Helper functions for expanding/collapsing rows in attendance list
import { isGroupReportLessonType } from "../../common/lessonTypes";

/**
 * Toggles the expansion state of a specific row
 * @param {string} groupId - The ID of the group to toggle
 * @param {Array} expandedRows - Current array of expanded row IDs
 * @param {Array} filteredAttendance - The filtered attendance data
 * @returns {Array} - Updated array of expanded row IDs
 */
export const toggleRowExpansion = (groupId, expandedRows, filteredAttendance) => {
  const isGroupInFilteredAttendance = filteredAttendance
    .map(reg => reg.id)
    .includes(groupId);

  if (!isGroupInFilteredAttendance) {
    return expandedRows;
  }

  if (expandedRows.includes(groupId)) {
    return expandedRows.filter(id => id !== groupId);
  }

  return [...expandedRows, groupId];
};

/**
 * Determines if all rows are expanded
 * @param {Array} expandedRows - Current array of expanded row IDs
 * @param {Array} filteredAttendance - The filtered attendance data
 * @returns {boolean} - True if all expandable rows are expanded
 */
export const areAllRowsExpanded = (expandedRows, filteredAttendance, lessonTypes) => {
  const expandableRowIds = filteredAttendance
    .filter(att => isGroupReportLessonType(att.classType, lessonTypes))
    .map(att => att.id);

  if (expandableRowIds.length === 0) {
    return true;
  }

  return expandableRowIds.every(id => expandedRows.includes(id));
};

/**
 * Gets all expandable row IDs from the filtered attendance data
 * @param {Array} filteredAttendance - The filtered attendance data
 * @returns {Array} - Array of expandable row IDs
 */
const getExpandableRowIds = (filteredAttendance, lessonTypes) => filteredAttendance
  .filter(reg => isGroupReportLessonType(reg.classType, lessonTypes))
  .map(reg => reg.id);

/**
 * Toggles between expanding all rows and collapsing all rows
 * @param {boolean} currentExpandState - Current state of expansion toggle
 * @param {Array} filteredAttendance - The filtered attendance data
 * @returns {Object} - Object containing new expanded rows array and toggle state
 */
export const toggleAllRows = (currentExpandState, filteredAttendance, lessonTypes) => {
  const expandableRowIds = getExpandableRowIds(filteredAttendance, lessonTypes);

  if (currentExpandState) {
    // If currently expanded, return all expandable rows
    return {
      expandedRows: expandableRowIds,
      expandedRowsToggle: false,
    };
  }

  // If currently collapsed, return empty array
  return {
    expandedRows: [],
    expandedRowsToggle: true,
  };
};
